// ============================================
// MEDFLOW - Professionals Page
// ============================================

import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Plus,
  Search,
  UserCog,
  Mail,
  Phone,
  Clock,
  Calendar as CalendarIcon,
  Stethoscope,
  Pencil,
  Bot,
  X,
  Zap,
  CheckCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Card, CardContent } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Input } from '@/components/ui/Input';
import { professionalsApi } from '@/lib/api';
import { cn, getInitials } from '@/lib/utils';

interface ServiceInfo {
  id: string;
  name: string;
  durationMinutes: number;
}

interface Professional {
  id: string;
  color: string;
  specialty: string;
  instagram?: string | null;
  linkedin?: string | null;
  isActive?: boolean;
  schedule?: Record<string, unknown[]>;
  user: { name: string; email: string; phone?: string };
  services?: ServiceInfo[];
}

interface ProfessionalFormState {
  fullName: string;
  email: string;
  phone: string;
  specialty: string;
  color: string;
  instagram: string;
  linkedin: string;
  isActive: boolean;
}

interface AgentConfig {
  id: string;
  professionalId: string;
  name: string;
  channel: 'whatsapp' | 'webchat' | 'instagram' | 'telegram';
  isActive: boolean;
  communicationTone?: string;
  faqItems: string[];
  lineIdentifier?: string;
  n8nWebhookUrl?: string;
}

interface AgentFormState {
  name: string;
  channel: 'whatsapp' | 'webchat' | 'instagram' | 'telegram';
  isActive: boolean;
  communicationTone: string;
  faqItemsText: string;
  lineIdentifier: string;
}

const DAYS_OF_WEEK = [
  { key: 'monday', label: 'Lun' },
  { key: 'tuesday', label: 'Mar' },
  { key: 'wednesday', label: 'Mie' },
  { key: 'thursday', label: 'Jue' },
  { key: 'friday', label: 'Vie' },
  { key: 'saturday', label: 'Sab' },
  { key: 'sunday', label: 'Dom' },
];

const TONE_OPTIONS = [
  {
    value: 'amigable' as const,
    label: 'Amigable',
    description: 'Cercano y empático. Ideal para clínicas con trato personal.',
    emoji: '😊',
  },
  {
    value: 'formal' as const,
    label: 'Formal',
    description: 'Profesional y respetuoso. Ideal para especialidades médicas.',
    emoji: '🎩',
  },
  {
    value: 'neutral' as const,
    label: 'Neutral',
    description: 'Claro y eficiente. Equilibrio entre calidez y profesionalismo.',
    emoji: '⚖️',
  },
];

const defaultProfessionalForm = (): ProfessionalFormState => ({
  fullName: '',
  email: '',
  phone: '',
  specialty: '',
  color: '#3B82F6',
  instagram: '',
  linkedin: '',
  isActive: true,
});

const defaultAgentForm = (): AgentFormState => ({
  name: '',
  channel: 'whatsapp',
  isActive: true,
  communicationTone: '',
  faqItemsText: '',
  lineIdentifier: '',
});

export function ProfessionalsPage() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingProfessional, setEditingProfessional] = useState<Professional | null>(null);
  const [form, setForm] = useState<ProfessionalFormState>(defaultProfessionalForm());
  const [agentProfessional, setAgentProfessional] = useState<Professional | null>(null);
  const [agentForm, setAgentForm] = useState<AgentFormState>(defaultAgentForm());
  const [n8nSyncResult, setN8nSyncResult] = useState<{ webhookUrl: string; action: string } | null>(null);

  // Quick activate state
  const [quickActivateProf, setQuickActivateProf] = useState<Professional | null>(null);
  const [quickActivateTone, setQuickActivateTone] = useState<'amigable' | 'formal' | 'neutral'>('amigable');
  const [quickActivateNotes, setQuickActivateNotes] = useState('');
  const [quickActivateSuccess, setQuickActivateSuccess] = useState<{ name: string; webhookUrl: string | null } | null>(null);

  const [provisionResult, setProvisionResult] = useState<{
    email: string;
    temporaryPassword: string | null;
    createdNewUser: boolean;
  } | null>(null);
  const [formMessage, setFormMessage] = useState<{ type: 'error' | 'success'; text: string } | null>(null);

  // ── Queries ──────────────────────────────────────────────────────────────
  const { data, isLoading } = useQuery({
    queryKey: ['professionals'],
    queryFn: async () => {
      const response = await professionalsApi.list(true);
      return (response.data as Professional[]) || [];
    },
  });

  const { data: agentConfigs = [] } = useQuery({
    queryKey: ['professional-agents'],
    queryFn: async () => {
      const response = await professionalsApi.listAgents();
      return (response.data as AgentConfig[]) || [];
    },
  });

  // ── Mutations ─────────────────────────────────────────────────────────────
  const createMutation = useMutation({
    mutationFn: async () => {
      if (!form.fullName.trim()) throw new Error('El nombre completo es obligatorio.');
      if (!form.specialty.trim()) throw new Error('La especialidad es obligatoria.');
      if (!form.email.trim()) throw new Error('El email de acceso es obligatorio.');
      return professionalsApi.provision({
        fullName: form.fullName.trim(),
        email: form.email.trim(),
        specialty: form.specialty.trim(),
        color: form.color,
        phone: form.phone.trim() || undefined,
      });
    },
    onSuccess: (res) => {
      const result = res.data;
      setFormMessage(null);
      setProvisionResult({
        email: result?.professional?.user?.email ?? form.email,
        temporaryPassword: result?.temporaryPassword ?? null,
        createdNewUser: result?.createdNewUser ?? false,
      });
      setShowCreateModal(false);
      setForm(defaultProfessionalForm());
      queryClient.invalidateQueries({ queryKey: ['professionals'] });
    },
    onError: (error: Error) => {
      setFormMessage({ type: 'error', text: error.message || 'No se pudo crear el profesional.' });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async () => {
      if (!editingProfessional) throw new Error('No hay profesional seleccionado.');
      return professionalsApi.update(editingProfessional.id, {
        specialty: form.specialty.trim(),
        color: form.color,
        isActive: form.isActive,
        instagram: form.instagram.trim() || null,
        linkedin: form.linkedin.trim() || null,
      });
    },
    onSuccess: () => {
      setFormMessage(null);
      setEditingProfessional(null);
      setForm(defaultProfessionalForm());
      queryClient.invalidateQueries({ queryKey: ['professionals'] });
    },
    onError: (error: Error) => {
      setFormMessage({ type: 'error', text: error.message || 'No se pudo actualizar el profesional.' });
    },
  });

  const quickActivateMutation = useMutation({
    mutationFn: async () => {
      if (!quickActivateProf) throw new Error('No hay profesional seleccionado.');
      return professionalsApi.activateAgent(quickActivateProf.id, {
        tone: quickActivateTone,
        specialNotes: quickActivateNotes.trim() || undefined,
      });
    },
    onSuccess: (res) => {
      const result = res.data;
      setQuickActivateSuccess({
        name: result?.agent?.name ?? 'Agente',
        webhookUrl: result?.webhookUrl ?? null,
      });
      queryClient.invalidateQueries({ queryKey: ['professional-agents'] });
      queryClient.invalidateQueries({ queryKey: ['professionals'] });
    },
    onError: (error: Error) => {
      setFormMessage({ type: 'error', text: error.message || 'No se pudo activar el agente.' });
    },
  });

  const saveAgentMutation = useMutation({
    mutationFn: async () => {
      if (!agentProfessional) throw new Error('No hay profesional seleccionado para el agente.');
      const faqItems = agentForm.faqItemsText
        .split('\n')
        .map((item) => item.trim())
        .filter(Boolean);
      await professionalsApi.upsertAgent(agentProfessional.id, {
        channel: agentForm.channel,
        name: agentForm.name.trim(),
        isActive: agentForm.isActive,
        communicationTone: agentForm.communicationTone.trim() || undefined,
        faqItems,
        lineIdentifier: agentForm.lineIdentifier.trim() || undefined,
      });
    },
    onSuccess: () => {
      setAgentProfessional(null);
      setAgentForm(defaultAgentForm());
      setN8nSyncResult(null);
      queryClient.invalidateQueries({ queryKey: ['professional-agents'] });
    },
  });

  const syncN8nMutation = useMutation({
    mutationFn: async () => {
      if (!agentProfessional) throw new Error('No hay profesional seleccionado.');
      const res = await professionalsApi.syncN8nWorkflow(agentProfessional.id);
      return res.data;
    },
    onSuccess: (data) => {
      if (data) setN8nSyncResult({ webhookUrl: data.webhookUrl, action: data.action });
    },
  });

  // ── Derived state ─────────────────────────────────────────────────────────
  const professionals = useMemo(() => data ?? [], [data]);
  const agentByProfessionalId = new Map(agentConfigs.map((agent) => [agent.professionalId, agent]));

  const filteredProfessionals = useMemo(() => {
    return professionals.filter((prof) => {
      const q = search.toLowerCase();
      return (
        prof.user.name.toLowerCase().includes(q) ||
        prof.specialty.toLowerCase().includes(q) ||
        prof.user.email.toLowerCase().includes(q)
      );
    });
  }, [professionals, search]);

  // ── Handlers ──────────────────────────────────────────────────────────────
  const openCreateModal = () => {
    setProvisionResult(null);
    setFormMessage(null);
    setEditingProfessional(null);
    setForm(defaultProfessionalForm());
    setShowCreateModal(true);
  };

  const openEditModal = (professional: Professional) => {
    setProvisionResult(null);
    setFormMessage(null);
    setShowCreateModal(false);
    setEditingProfessional(professional);
    setForm({
      fullName: professional.user.name,
      email: professional.user.email,
      phone: professional.user.phone ?? '',
      specialty: professional.specialty,
      color: professional.color,
      instagram: professional.instagram ?? '',
      linkedin: professional.linkedin ?? '',
      isActive: professional.isActive ?? true,
    });
  };

  const closeModal = () => {
    setShowCreateModal(false);
    setEditingProfessional(null);
    setFormMessage(null);
    setForm(defaultProfessionalForm());
  };

  const openAgentModal = (professional: Professional) => {
    const currentAgent = agentByProfessionalId.get(professional.id);
    setN8nSyncResult(null);
    setAgentProfessional(professional);
    setAgentForm(
      currentAgent
        ? {
            name: currentAgent.name,
            channel: currentAgent.channel,
            isActive: currentAgent.isActive,
            communicationTone: currentAgent.communicationTone ?? '',
            faqItemsText: (currentAgent.faqItems ?? []).join('\n'),
            lineIdentifier: currentAgent.lineIdentifier ?? '',
          }
        : {
            ...defaultAgentForm(),
            name: `Agente ${professional.user.name}`,
          }
    );
  };

  const closeAgentModal = () => {
    setAgentProfessional(null);
    setAgentForm(defaultAgentForm());
  };

  const openQuickActivate = (professional: Professional) => {
    setQuickActivateProf(professional);
    setQuickActivateTone('amigable');
    setQuickActivateNotes('');
    setQuickActivateSuccess(null);
  };

  const closeQuickActivate = () => {
    setQuickActivateProf(null);
    setQuickActivateSuccess(null);
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Profesionales</h1>
          <p className="text-gray-600">{professionals.length} profesionales registrados</p>
        </div>
        <Button onClick={openCreateModal}>
          <Plus className="w-4 h-4 mr-2" />
          Agregar profesional
        </Button>
      </div>

      {provisionResult && (
        <Card>
          <CardContent className="flex flex-col gap-2 p-4 bg-green-50 border border-green-200 text-green-900 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="font-medium">Profesional creado correctamente</p>
              <p className="text-sm">
                Cuenta: <strong>{provisionResult.email}</strong>
                {provisionResult.createdNewUser && provisionResult.temporaryPassword
                  ? ` · contraseña temporal: `
                  : ' · el usuario ya existía y fue asociado'}
                {provisionResult.temporaryPassword && (
                  <code className="font-mono bg-green-100 px-1 rounded">{provisionResult.temporaryPassword}</code>
                )}
              </p>
            </div>
            <Button variant="outline" size="sm" onClick={() => setProvisionResult(null)}>
              Cerrar
            </Button>
          </CardContent>
        </Card>
      )}

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type="text"
          placeholder="Buscar por nombre, especialidad o email..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full h-10 pl-10 pr-4 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary"
        />
      </div>

      {isLoading ? (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-64 bg-gray-100 rounded-lg animate-pulse" />
          ))}
        </div>
      ) : filteredProfessionals.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <UserCog className="w-12 h-12 text-gray-300 mb-4" />
            <p className="text-gray-500 text-center">
              {search ? 'No se encontraron profesionales' : 'No hay profesionales registrados'}
            </p>
            <Button className="mt-4" onClick={openCreateModal}>
              <Plus className="w-4 h-4 mr-2" />
              Agregar profesional
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {filteredProfessionals.map((prof) => (
            <Card key={prof.id} className="overflow-hidden">
              <div className="h-2" style={{ backgroundColor: prof.color }} />
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4 gap-3">
                  <div className="flex items-center gap-3">
                    <div
                      className="w-12 h-12 rounded-full flex items-center justify-center text-white font-semibold"
                      style={{ backgroundColor: prof.color }}
                    >
                      {getInitials(prof.user.name)}
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">{prof.user.name}</h3>
                      <p className="text-sm text-gray-500">{prof.specialty}</p>
                    </div>
                  </div>
                  <Badge className={prof.isActive === false ? 'bg-gray-100 text-gray-700' : 'bg-green-100 text-green-800'}>
                    {prof.isActive === false ? 'Inactivo' : 'Activo'}
                  </Badge>
                </div>

                {agentByProfessionalId.get(prof.id) && (
                  <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-900 flex items-center justify-between gap-2">
                    <span className="flex items-center gap-2">
                      <Bot className="w-3.5 h-3.5" />
                      {agentByProfessionalId.get(prof.id)?.isActive ? 'Agente activo' : 'Agente configurado pero inactivo'}
                    </span>
                    <span className="uppercase tracking-wide">{agentByProfessionalId.get(prof.id)?.channel}</span>
                  </div>
                )}

                <div className="space-y-2 mb-4">
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Mail className="w-4 h-4" />
                    <span className="truncate">{prof.user.email}</span>
                  </div>
                  {prof.user.phone && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Phone className="w-4 h-4" />
                      <span>{prof.user.phone}</span>
                    </div>
                  )}
                </div>

                {prof.services && prof.services.length > 0 && (
                  <div className="border-t pt-4">
                    <p className="text-xs font-medium text-gray-500 uppercase mb-2 flex items-center gap-1">
                      <Stethoscope className="w-3 h-3" />
                      Servicios ({prof.services.length})
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {prof.services.map((svc) => (
                        <Badge key={svc.id} className="bg-gray-100 text-gray-700 text-xs font-normal">
                          {svc.name}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                <div className="border-t pt-4">
                  <p className="text-xs font-medium text-gray-500 uppercase mb-2 flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    Horario semanal
                  </p>
                  <div className="flex gap-1">
                    {DAYS_OF_WEEK.map((day) => {
                      const hasSchedule = prof.schedule?.[day.key] && (prof.schedule[day.key] as unknown[]).length > 0;
                      return (
                        <div
                          key={day.key}
                          className={cn(
                            'flex-1 text-center py-1 rounded text-xs font-medium',
                            hasSchedule ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-400'
                          )}
                        >
                          {day.label}
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="flex gap-2 mt-4">
                  <Button variant="outline" size="sm" className="flex-1">
                    <CalendarIcon className="w-4 h-4 mr-1" />
                    Ver agenda
                  </Button>
                  {agentByProfessionalId.has(prof.id) ? (
                    <Button variant="outline" size="sm" className="flex-1" onClick={() => openAgentModal(prof)}>
                      <Bot className="w-4 h-4 mr-1" />
                      Configurar
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      className="flex-1 bg-primary/10 text-primary border border-primary/20 hover:bg-primary/20"
                      onClick={() => openQuickActivate(prof)}
                    >
                      <Zap className="w-4 h-4 mr-1" />
                      Crear agente
                    </Button>
                  )}
                  <Button variant="outline" size="sm" className="flex-1" onClick={() => openEditModal(prof)}>
                    <Pencil className="w-4 h-4 mr-1" />
                    Editar
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* ── Quick Activate Modal ──────────────────────────────────────────── */}
      {quickActivateProf && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-2xl bg-white shadow-xl flex flex-col">

            <div className="flex items-center justify-between border-b px-6 py-4">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  Activar agente · {quickActivateProf.user.name}
                </h2>
                <p className="text-sm text-gray-500">El sistema configurará todo automáticamente.</p>
              </div>
              <Button variant="ghost" size="icon" onClick={closeQuickActivate}>
                <X className="w-4 h-4" />
              </Button>
            </div>

            {quickActivateSuccess ? (
              <div className="px-6 py-8 flex flex-col items-center gap-4 text-center">
                <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center">
                  <CheckCircle className="w-7 h-7 text-green-600" />
                </div>
                <div>
                  <p className="font-semibold text-gray-900">{quickActivateSuccess.name} activado</p>
                  {quickActivateSuccess.webhookUrl ? (
                    <p className="text-xs text-gray-500 mt-1">
                      Workflow n8n sincronizado. Puedes probar el agente en{' '}
                      <strong>Agentes IA</strong>.
                    </p>
                  ) : (
                    <p className="text-xs text-gray-500 mt-1">
                      Agente guardado. Sincroniza el workflow n8n desde <strong>Configurar agente</strong>.
                    </p>
                  )}
                </div>
                <Button onClick={closeQuickActivate} className="w-full">Cerrar</Button>
              </div>
            ) : (
              <>
                <div className="px-6 py-5 space-y-4">
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-gray-700">Tono del agente</p>
                    <div className="grid gap-2">
                      {TONE_OPTIONS.map((opt) => (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => setQuickActivateTone(opt.value)}
                          className={cn(
                            'flex items-start gap-3 rounded-xl border px-4 py-3 text-left transition-colors',
                            quickActivateTone === opt.value
                              ? 'border-primary bg-primary/5'
                              : 'border-gray-200 hover:border-gray-300'
                          )}
                        >
                          <span className="text-xl mt-0.5">{opt.emoji}</span>
                          <div>
                            <p className="text-sm font-medium text-gray-900">{opt.label}</p>
                            <p className="text-xs text-gray-500">{opt.description}</p>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">
                      Notas adicionales <span className="text-gray-400 font-normal">(opcional)</span>
                    </label>
                    <textarea
                      rows={2}
                      value={quickActivateNotes}
                      onChange={(e) => setQuickActivateNotes(e.target.value)}
                      placeholder="Ej: No agenda los viernes. Solo consulta de adultos."
                      className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm"
                    />
                  </div>

                  {quickActivateMutation.isError && (
                    <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                      {quickActivateMutation.error instanceof Error
                        ? quickActivateMutation.error.message
                        : 'Error al activar el agente'}
                    </div>
                  )}
                </div>

                <div className="flex justify-end gap-3 border-t px-6 py-4">
                  <Button variant="outline" onClick={closeQuickActivate}>Cancelar</Button>
                  <Button
                    onClick={() => quickActivateMutation.mutate()}
                    loading={quickActivateMutation.isPending}
                  >
                    <Zap className="w-4 h-4 mr-2" />
                    Activar agente
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ── Advanced Agent Config Modal ───────────────────────────────────── */}
      {agentProfessional && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-2xl rounded-2xl bg-white shadow-xl flex flex-col max-h-[88vh]">

            <div className="flex items-center justify-between border-b px-6 py-4 shrink-0">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  {agentByProfessionalId.has(agentProfessional.id) ? 'Configurar agente' : 'Crear agente'} · {agentProfessional.user.name}
                </h2>
                <p className="text-sm text-gray-500">Recepcionista virtual del profesional.</p>
              </div>
              <Button variant="ghost" size="icon" onClick={closeAgentModal}>
                <X className="w-4 h-4" />
              </Button>
            </div>

            <div className="overflow-y-auto flex-1 px-6 py-5">
              <div className="grid gap-4 md:grid-cols-2">
                <Input
                  label="Nombre del agente"
                  value={agentForm.name}
                  onChange={(e) => setAgentForm((f) => ({ ...f, name: e.target.value }))}
                  placeholder="Recepcionista Dra. Pérez"
                />
                <label className="space-y-2">
                  <span className="text-sm font-medium text-gray-700">Canal</span>
                  <select
                    value={agentForm.channel}
                    onChange={(e) => setAgentForm((f) => ({ ...f, channel: e.target.value as AgentFormState['channel'] }))}
                    className="flex h-10 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm"
                  >
                    <option value="whatsapp">WhatsApp</option>
                    <option value="webchat">Webchat</option>
                    <option value="instagram">Instagram</option>
                    <option value="telegram">Telegram</option>
                  </select>
                </label>
                <label className="space-y-2">
                  <span className="text-sm font-medium text-gray-700">Tono de comunicación</span>
                  <select
                    value={agentForm.communicationTone}
                    onChange={(e) => setAgentForm((f) => ({ ...f, communicationTone: e.target.value }))}
                    className="flex h-10 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm"
                  >
                    <option value="">Seleccionar tono...</option>
                    {TONE_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.emoji} {opt.label} — {opt.description}
                      </option>
                    ))}
                  </select>
                </label>
                <Input
                  label="Línea de contacto"
                  value={agentForm.lineIdentifier}
                  onChange={(e) => setAgentForm((f) => ({ ...f, lineIdentifier: e.target.value }))}
                  placeholder="WhatsApp +56 9 1234 5678"
                />
                <label className="flex items-center gap-3 rounded-xl border px-4 py-3">
                  <input
                    type="checkbox"
                    checked={agentForm.isActive}
                    onChange={(e) => setAgentForm((f) => ({ ...f, isActive: e.target.checked }))}
                  />
                  <span className="text-sm text-gray-700">Agente activo</span>
                </label>
              </div>

              <div className="mt-5">
                <label className="space-y-2">
                  <span className="text-sm font-medium text-gray-700">Preguntas frecuentes (una por línea)</span>
                  <textarea
                    rows={5}
                    value={agentForm.faqItemsText}
                    onChange={(e) => setAgentForm((f) => ({ ...f, faqItemsText: e.target.value }))}
                    placeholder={"¿Atiendes con Fonasa?\n¿Cuánto dura la primera evaluación?\n¿Dónde está ubicada la clínica?"}
                    className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm"
                  />
                </label>
              </div>

              {n8nSyncResult && (
                <div className="mt-4 rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-800">
                  <span className="font-medium">
                    Workflow n8n {n8nSyncResult.action === 'created' ? 'creado' : 'actualizado'}:
                  </span>{' '}
                  <code className="break-all text-xs">{n8nSyncResult.webhookUrl}</code>
                </div>
              )}
              {syncN8nMutation.isError && (
                <div className="mt-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                  {syncN8nMutation.error instanceof Error ? syncN8nMutation.error.message : 'Error al sincronizar con n8n'}
                </div>
              )}

              {agentProfessional.services && agentProfessional.services.length > 0 && (
                <div className="mt-4 rounded-lg border border-gray-100 bg-gray-50 px-4 py-3">
                  <p className="text-xs font-medium text-gray-500 uppercase mb-2">Tratamientos vinculados</p>
                  <div className="flex flex-wrap gap-1">
                    {agentProfessional.services.map((svc) => (
                      <Badge key={svc.id} className="bg-white text-gray-700 text-xs font-normal border">
                        {svc.name}
                      </Badge>
                    ))}
                  </div>
                  <p className="text-xs text-gray-400 mt-2">
                    Se heredan del perfil del profesional. Edítalos desde Servicios.
                  </p>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 border-t px-6 py-4 shrink-0">
              <Button variant="outline" onClick={closeAgentModal}>Cancelar</Button>
              <Button
                variant="outline"
                onClick={() => syncN8nMutation.mutate()}
                loading={syncN8nMutation.isPending}
                disabled={!agentForm.name.trim() || saveAgentMutation.isPending}
              >
                Sincronizar n8n
              </Button>
              <Button
                onClick={() => saveAgentMutation.mutate()}
                loading={saveAgentMutation.isPending}
                disabled={!agentForm.name.trim()}
              >
                Guardar agente
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ── Create / Edit Professional Modal ─────────────────────────────── */}
      {(showCreateModal || editingProfessional) && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-lg rounded-2xl bg-white shadow-xl">
            <div className="flex items-center justify-between border-b px-6 py-4">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  {editingProfessional ? 'Editar profesional' : 'Agregar profesional'}
                </h2>
                <p className="text-sm text-gray-500">
                  {editingProfessional
                    ? 'Actualiza los datos del profesional.'
                    : 'Se creará la cuenta con una contraseña temporal.'}
                </p>
              </div>
              <Button variant="ghost" size="icon" onClick={closeModal}>
                <X className="w-4 h-4" />
              </Button>
            </div>

            <div className="grid gap-4 px-6 py-5 md:grid-cols-2">
              {formMessage && (
                <div
                  className={cn(
                    'md:col-span-2 rounded-lg border px-3 py-2 text-sm',
                    formMessage.type === 'error'
                      ? 'border-red-200 bg-red-50 text-red-700'
                      : 'border-green-200 bg-green-50 text-green-700'
                  )}
                >
                  {formMessage.text}
                </div>
              )}

              {!editingProfessional ? (
                <>
                  <Input
                    label="Nombre completo"
                    value={form.fullName}
                    onChange={(e) => setForm((f) => ({ ...f, fullName: e.target.value }))}
                    placeholder="Dra. Ana Pérez"
                  />
                  <Input
                    label="Especialidad"
                    value={form.specialty}
                    onChange={(e) => setForm((f) => ({ ...f, specialty: e.target.value }))}
                    placeholder="Odontología"
                  />
                  <Input
                    label="Email de acceso"
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                    placeholder="doctor@clinica.com"
                  />
                  <Input
                    label="Teléfono"
                    value={form.phone}
                    onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                    placeholder="+56 9 1234 5678"
                  />
                  <Input
                    label="Color calendario"
                    type="color"
                    value={form.color}
                    onChange={(e) => setForm((f) => ({ ...f, color: e.target.value }))}
                  />
                </>
              ) : (
                <>
                  <Input
                    label="Especialidad"
                    value={form.specialty}
                    onChange={(e) => setForm((f) => ({ ...f, specialty: e.target.value }))}
                    placeholder="Odontología"
                  />
                  <Input
                    label="Color calendario"
                    type="color"
                    value={form.color}
                    onChange={(e) => setForm((f) => ({ ...f, color: e.target.value }))}
                  />
                  <Input
                    label="Instagram (URL o @usuario)"
                    value={form.instagram}
                    onChange={(e) => setForm((f) => ({ ...f, instagram: e.target.value }))}
                    placeholder="https://instagram.com/dr.perez o @dr.perez"
                  />
                  <Input
                    label="LinkedIn (URL)"
                    value={form.linkedin}
                    onChange={(e) => setForm((f) => ({ ...f, linkedin: e.target.value }))}
                    placeholder="https://linkedin.com/in/dr-perez"
                  />
                  <label className="flex items-center gap-3 rounded-xl border px-4 py-3 mt-2 md:col-span-2">
                    <input
                      type="checkbox"
                      checked={form.isActive}
                      onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))}
                    />
                    <span className="text-sm text-gray-700">Profesional activo</span>
                  </label>
                </>
              )}
            </div>

            <div className="flex justify-end gap-3 border-t px-6 py-4">
              <Button variant="outline" onClick={closeModal}>Cancelar</Button>
              <Button
                onClick={() => {
                  if (editingProfessional) {
                    updateMutation.mutate();
                  } else {
                    createMutation.mutate();
                  }
                }}
                disabled={
                  !editingProfessional &&
                  (!form.fullName.trim() || !form.specialty.trim() || !form.email.trim())
                }
                loading={createMutation.isPending || updateMutation.isPending}
              >
                {editingProfessional ? 'Guardar cambios' : 'Crear profesional'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
